import React, { useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

const DATE_FIELDS = ['garantiBaslangicTarihi', 'standartGarantiBitisTarihi', 'faturaTarihi', 'montajCikisTarih', 'uzatilmisGarantiBitisTarihi', 'bataryaStandartGarantiBitisTarihi', 'bataryaUzatilmisGarantiBitisTarihi'];

const TEXT_FIELDS = ['sasiNo', 'pypKop', 'model', 'urunAciklama', 'garantiKm', 'garantiMonth', 'musteriBilgisi', 'bayiBilgisi', 'sevkUlke'];

const VehicleParkReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();

    /* ------------------------------------------------------------------ */
    /*  Match-mode option lists                                             */
    /* ------------------------------------------------------------------ */
    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    /* ------------------------------------------------------------------ */
    /*  Date filter template (Calendar)                                     */
    /* ------------------------------------------------------------------ */
    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let normalizedDate = e.value;
            if (normalizedDate instanceof Date) {
                normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
            }
            options.filterApplyCallback(normalizedDate);
        };

        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon showButtonBar className="p-column-filter" style={{ minWidth: '12rem' }} />;
    };

    /* ------------------------------------------------------------------ */
    /*  Column builder helpers                                              */
    /* ------------------------------------------------------------------ */
    const makeTextColumn = (field, headerKey, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body,
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const makeDateColumn = (field, headerKey, options = {}) => {
        const { sortable = true, minWidth = '180px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body: (row) => formatDateOnly(row?.[field]),
            filter: true,
            filterField: field,
            filterElement: dateFilterTemplate,
            showFilterMenu: false,
            showClearButton: false
        };
    };

    /* ------------------------------------------------------------------ */
    /*  tableFilters – synced with activeFilters state                      */
    /* ------------------------------------------------------------------ */
    const tableFilters = useMemo(
        () => ({
            /* text fields */
            sasiNo: {
                value: activeFilters?.sasiNo ?? null,
                matchMode: activeFilters?.sasiNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            pypKop: {
                value: activeFilters?.pypKop ?? null,
                matchMode: activeFilters?.pypKopMatchMode ?? FilterMatchMode.CONTAINS
            },
            model: {
                value: activeFilters?.model ?? null,
                matchMode: activeFilters?.modelMatchMode ?? FilterMatchMode.CONTAINS
            },
            urunAciklama: {
                value: activeFilters?.urunAciklama ?? null,
                matchMode: activeFilters?.urunAciklamaMatchMode ?? FilterMatchMode.CONTAINS
            },
            garantiMonth: {
                value: activeFilters?.garantiMonth ?? null,
                matchMode: activeFilters?.garantiMonthMatchMode ?? FilterMatchMode.CONTAINS
            },
            musteriBilgisi: {
                value: activeFilters?.musteriBilgisi ?? null,
                matchMode: activeFilters?.musteriBilgisiMatchMode ?? FilterMatchMode.CONTAINS
            },
            bayiBilgisi: {
                value: activeFilters?.bayiBilgisi ?? null,
                matchMode: activeFilters?.bayiBilgisiMatchMode ?? FilterMatchMode.CONTAINS
            },
            sevkUlke: {
                value: activeFilters?.sevkUlke ?? null,
                matchMode: activeFilters?.sevkUlkeMatchMode ?? FilterMatchMode.CONTAINS
            },
            /* date fields */
            garantiBaslangicTarihi: {
                value: activeFilters?.garantiBaslangicTarihi ?? null,
                matchMode: activeFilters?.garantiBaslangicTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            standartGarantiBitisTarihi: {
                value: activeFilters?.standartGarantiBitisTarihi ?? null,
                matchMode: activeFilters?.standartGarantiBitisTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            faturaTarihi: {
                value: activeFilters?.faturaTarihi ?? null,
                matchMode: activeFilters?.faturaTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            montajCikisTarih: {
                value: activeFilters?.montajCikisTarih ?? null,
                matchMode: activeFilters?.montajCikisTarihMatchMode ?? FilterMatchMode.DATE_IS
            },
            uzatilmisGarantiBitisTarihi: {
                value: activeFilters?.uzatilmisGarantiBitisTarihi ?? null,
                matchMode: activeFilters?.uzatilmisGarantiBitisTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            bataryaStandartGarantiBitisTarihi: {
                value: activeFilters?.bataryaStandartGarantiBitisTarihi ?? null,
                matchMode: activeFilters?.bataryaStandartGarantiBitisTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            bataryaUzatilmisGarantiBitisTarihi: {
                value: activeFilters?.bataryaUzatilmisGarantiBitisTarihi ?? null,
                matchMode: activeFilters?.bataryaUzatilmisGarantiBitisTarihiMatchMode ?? FilterMatchMode.DATE_IS
            },
            garantiKm: {
                value: activeFilters?.garantiKm ?? null,
                matchMode: activeFilters?.garantiKmMatchMode ?? FilterMatchMode.CONTAINS
            }
        }),
        [activeFilters]
    );

    /* ------------------------------------------------------------------ */
    /*  handleTableFilter – maps PrimeReact filter events to activeFilters  */
    /* ------------------------------------------------------------------ */
    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = { ...(activeFilters || {}) };

            /* text fields */
            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });

            /* date fields */
            DATE_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.DATE_IS;
                }
            });

            onFiltersChange?.(updated);
        },
        [activeFilters, onFiltersChange]
    );

    /* ------------------------------------------------------------------ */
    /*  Misc templates                                                      */
    /* ------------------------------------------------------------------ */
    const handleSort = (e) => onSortChange?.(e);

    const columns = [
        makeTextColumn('sasiNo', 'vehicleParkReport.columns.sasiNo', { minWidth: '160px' }),
        makeTextColumn('pypKop', 'vehicleParkReport.columns.pypKop', { minWidth: '120px' }),
        makeTextColumn('model', 'vehicleParkReport.columns.model', { minWidth: '120px' }),
        makeTextColumn('urunAciklama', 'vehicleParkReport.columns.urunAciklama', {
            minWidth: '200px',
            body: (rowData) => createTruncatedTextForTable(rowData.urunAciklama, 30, `urun-${rowData.sasiNo}`)
        }),
        makeTextColumn('garantiKm', 'vehicleParkReport.columns.garantiKm', { minWidth: '130px' }),
        makeDateColumn('garantiBaslangicTarihi', 'vehicleParkReport.columns.garantiBaslangicTarihi', { minWidth: '180px' }),
        makeDateColumn('standartGarantiBitisTarihi', 'vehicleParkReport.columns.standartGarantiBitisTarihi', { minWidth: '180px' }),
        makeDateColumn('montajCikisTarih', 'vehicleParkReport.columns.montajCikisTarih', { minWidth: '160px' }),
        makeDateColumn('uzatilmisGarantiBitisTarihi', 'vehicleParkReport.columns.uzatilmisGarantiBitisTarihi', { minWidth: '200px' }),
        makeDateColumn('bataryaStandartGarantiBitisTarihi', 'vehicleParkReport.columns.bataryaStandartGarantiBitisTarihi', { minWidth: '220px' }),
        makeDateColumn('bataryaUzatilmisGarantiBitisTarihi', 'vehicleParkReport.columns.bataryaUzatilmisGarantiBitisTarihi', { minWidth: '220px' }),
        makeDateColumn('faturaTarihi', 'vehicleParkReport.columns.faturaTarihi', { minWidth: '150px' }),
        makeTextColumn('musteriBilgisi', 'vehicleParkReport.columns.musteriBilgisi', {
            minWidth: '200px',
            body: (rowData) => createTruncatedTextForTable(rowData.musteriBilgisi, 30, `musteri-${rowData.sasiNo}`)
        }),
        makeTextColumn('bayiBilgisi', 'vehicleParkReport.columns.bayiBilgisi', { minWidth: '180px' }),
        makeTextColumn('sevkUlke', 'vehicleParkReport.columns.sevkUlke', { minWidth: '120px' }),
        makeTextColumn('garantiMonth', 'vehicleParkReport.columns.garantiMonth', { minWidth: '160px' })
    ];

    return (
        <StickyScrollTable deps={[items, totalCount, loading, activeFilters]} scrollBarPosition="top">
        <PreferencesDataTable
            tableKey="vehicle-park-report"
            columns={columns}
            value={items}
            totalRecords={totalCount}
            className="p-datatable-gridlines"
            showGridlines
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 10}
            onPage={onPage}
            dataKey="sasiNo"
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 20, 50, 100]}
            tableStyle={{ minWidth: '80rem' }}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
            pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
        />
        </StickyScrollTable>
    );
};

export default VehicleParkReportTable;
